import React from "react";
import useLang from "Shared/hooks/useLanguage";
import PathologyReportSelect from "../PathologyReportSelect";
import {
  reportStatusOptions,
  signingPathologistOptions,
  type SelectOption,
} from "../pathologyReportData";

type Props = {
  signingPathologist: SelectOption | null;
  reportStatus: SelectOption | null;
  additionalPhysicians: string;
  onSigningPathologistChange: (v: SelectOption | null) => void;
  onReportStatusChange: (v: SelectOption | null) => void;
  onAdditionalPhysiciansChange: (v: string) => void;
};

const STATS = [
  { label: "Specimens", value: "3" },
  { label: "Synoptic fields", value: "10/20" },
  { label: "IHC markers", value: "2" },
  { label: "Addenda", value: "0" },
];

const SPECIMEN_PARTS = [
  { letter: "A", synoptic: null },
  { letter: "B", synoptic: null },
  { letter: "C", synoptic: "Synoptic: Colon / Rectum" },
];

const SignOutTab = ({
  signingPathologist,
  reportStatus,
  additionalPhysicians,
  onSigningPathologistChange,
  onReportStatusChange,
  onAdditionalPhysiciansChange,
}: Props) => {
  const { t } = useLang();

  return (
    <>
      <style>{`
        .pathology-sign-out-panel .sign-out-title {
          font-weight: 700;
          font-size: 1.05rem;
          color: #181c32;
          margin-bottom: 1.25rem;
        }
        .pathology-sign-out-cont {
          background-color: #f3f6f9;
          border: 1px solid #dce3ed;
          border-radius: 10px;
          padding: 14px 16px;  
        }
        .pathology-sign-out-stats {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 12px;
          margin-bottom: 1rem;
        }
        @media (max-width: 768px) {
          .pathology-sign-out-stats {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }
        .pathology-sign-out-stat-box {
          background: #fff;
          border: 1px solid #bebebe;
          border-radius: 8px;
          padding: 10px;
        }
        .pathology-sign-out-stat-box .stat-label {
          color: #a1a5b7;
          font-size: 0.8rem;
          font-weight: 500;
          margin-bottom: 6px;
        }
        .pathology-sign-out-stat-box .stat-value {
          color: #181c32;
          font-size: 1.75rem;
          font-weight: 700;
          line-height: 1.2;
        }
        .pathology-sign-out-specimen {
          display: flex;
          align-items: center;
          gap: 12px;
          background: #fff;
          border: 1px solid #bebebe;
          border-radius: 8px;
          padding: 8px;
          margin-bottom: 10px;
        }
        .pathology-sign-out-specimen:last-of-type {
          margin-bottom: 1.25rem;
        }
        .pathology-sign-out-specimen-letter {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 30px;
          height: 30px;
          min-width: 30px;
          background-color: #66bb6a;
          color: #fff;
          font-weight: 700;
          font-size: 0.9rem;
          border-radius: 4px;
        }
        .pathology-sign-out-specimen-name {
          font-weight: 600;
          color: #3f4254;
          font-size: 0.95rem;
        }
        .pathology-sign-out-synoptic-tag {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background-color: #e9ebf0;
          padding: 4px 10px;
          border-radius: 50rem;
          border: none;
          margin-left: 4px;
          line-height: 1.25;
        }
        .pathology-sign-out-panel .form-label {
          color: #181c32;
          font-weight: 600;
          font-size: 0.9rem;
          margin-bottom: 0.5rem;
        }
        .pathology-sign-out-btn-draft {
          background-color: #ffa726 !important;
          border-color: #ffa726 !important;
          color: #fff !important;
          font-weight: 600;
          padding: 0.55rem 1.5rem;
          border-radius: 0.475rem;
        }
        .pathology-sign-out-btn-draft:hover {
          background-color: #fb8c00 !important;
          border-color: #fb8c00 !important;
          color: #fff !important;
        }
        .pathology-sign-out-footer {
          color: #a1a5b7;
          font-size: 0.8rem;
          font-style: italic;
          margin-top: 1rem;
          margin-bottom: 0;
          line-height: 1.5;
        }
      `}</style>

      <div className="pathology-sign-out-panel">
        <div className="sign-out-title">{t("Review & electronic sign-out")}</div>

        <div className="pathology-sign-out-cont">
          <div className="pathology-sign-out-stats">
            {STATS.map((stat) => (
              <div key={stat.label} className="pathology-sign-out-stat-box">
                <div className="stat-label">{t(stat.label)}</div>
                <div className="stat-value">{stat.value}</div>
              </div>
            ))}
          </div>

          {SPECIMEN_PARTS.map((part) => (
            <div key={part.letter} className="pathology-sign-out-specimen">
              <span className="pathology-sign-out-specimen-letter">
                {part.letter}
              </span>
              <span className="pathology-sign-out-specimen-name">
                {t(`Part ${part.letter}`)}
              </span>
              {part.synoptic && (
                <span className="pathology-sign-out-synoptic-tag">
                  {t(part.synoptic)}
                </span>
              )}
            </div>
          ))}
        </div>

        <div className="row g-4 mb-4 mt-2">
          <div className="col-md-6">
            <label className="form-label">{t("Signing pathologist")}</label>
            <PathologyReportSelect
              value={signingPathologist}
              onChange={onSigningPathologistChange}
              options={signingPathologistOptions}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">{t("Report status")}</label>
            <PathologyReportSelect
              value={reportStatus}
              onChange={onReportStatusChange}
              options={reportStatusOptions}
            />
          </div>
          <div className="col-12">
            <label className="form-label">{t("Additional physician(s)")}</label>
            <input
              type="text"
              className="form-control rounded-2"
              placeholder={t("e.g. Dr. Mark Trahan (for consultation cases)")}
              value={additionalPhysicians}
              onChange={(e) => onAdditionalPhysiciansChange(e.target.value)}
            />
          </div>
        </div>

        <div className="d-flex flex-wrap gap-3">
          <button
            type="button"
            className="btn btn-primary fw-semibold px-5"
          >
            {t("Electronically sign & finalize")}
          </button>
          <button type="button" className="btn pathology-sign-out-btn-draft">
            {t("Save draft")}
          </button>
        </div>

        <p className="pathology-sign-out-footer">
          {t(
            "Electronically signed reports are final and will be delivered via HL7, PDF, and patient portal. Amendments require a new addendum."
          )}
        </p>
      </div>
    </>
  );
};

export default SignOutTab;
